import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import type { Task } from "../../../api/src/tasks/tasks.service";
import { NotificationContext } from "../Notifications";
import { useErrorMessage } from "./FormError";

/**
 * The submit half the create-task forms share: it turns every outcome of
 * `onCreate` into something the advisor can see.
 *
 * A null result means no CRM arm created anything (`createTask`,
 * api/src/tasks/tasks.service.ts) — an unmapped household, a Slant household
 * with no book_id, a disconnected CRM. That used to clear the form and say
 * nothing, which reads exactly like success (APP-1415). It has to reach a
 * notification rather than the form: the pane closes on submit, so an in-form
 * message would be hidden along with it. A thrown error leaves the pane open,
 * so that one stays in the form.
 */
export const useCreateTaskSubmit = <TTask extends { name: string }>({
  onCreate,
  reset,
}: {
  onCreate: (data: TTask) => Promise<Task | null>;
  reset: () => void;
}) => {
  const { setError, resetError, errorMessage } = useErrorMessage();
  const notificationContext = useContext(NotificationContext);

  const onSubmit = useMutation({
    mutationFn: async (data: TTask) => {
      resetError();

      try {
        const body = await onCreate(data);

        if (body === null) {
          notificationContext.pushNotification({
            id: "task-new",
            header: "Failed to Create Task",
            body: `${data.name} was not created`,
            variant: "danger",
          });

          return;
        }

        notificationContext.pushNotification({
          id: `task-${body.id}`,
          header: "Task Created",
          body: `${body.name} created`,
          variant: "success",
        });
        reset();
      } catch (err) {
        const message = "Failed to save task";
        console.error(message, err);
        setError(message);
      }
    },
  });

  return { errorMessage, submit: onSubmit.mutateAsync };
};
